import { z } from "zod";

// zodObjectIdentity is a helper because zod evaluates .unknown() as optional, but the value is not optional.
// https://github.com/colinhacks/zod/issues/1628#issuecomment-1336678982
export type ZodObjectIdentity<T extends z.ZodRawShape, Side extends "_input" | "_output"> = {
  [K in keyof T]: T[K][Side];
};

export function zodObjectIdentity<T extends z.ZodRawShape>(
  t: T,
): z.ZodObject<T, "strip", z.ZodTypeAny, ZodObjectIdentity<T, "_output">, ZodObjectIdentity<T, "_input">> {
  return z.object(t);
}

export const DatatableStateSchema = z.object({
  showColumnHeaders: z.boolean(),
  sectionColumnId: z.string().nullable().optional(),
  subSectionColumnId: z.string().nullable().optional(),
  globalFilter: z.string(),
  columnFilters: z.array(
    zodObjectIdentity({
      id: z.string(),
      value: z.unknown().transform((value) => {
        // (1) Ensure no __typename properties are included to enable comparison
        // (2) Ensure output type is still unknown (not Record<string, unknown>) to avoid type inference issues
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          const cleanedValue: Record<string, unknown> = {};
          for (const key in value as Record<string, unknown>) {
            if (!key.startsWith("__")) {
              cleanedValue[key] = (value as Record<string, unknown>)[key];
            }
          }
          return cleanedValue as unknown;
        }
        return value;
      }),
    }),
  ),
  columnOrder: z.array(z.string()),
  columnVisibility: z.record(z.string(), z.boolean()),
  sorting: z.array(
    z.object({
      id: z.string(),
      desc: z.boolean(),
    }),
  ),
  expanded: z.union([z.literal(true), z.record(z.string(), z.boolean())]),
});

export const getCleanedDatatableState = (state: DatatableState) => {
  const result = DatatableStateSchema.safeParse(state);
  if (!result.success) {
    return null;
  }
  return {
    ...result.data,
    columnFilters: result.data.columnFilters.map((filter) => ({
      ...filter,
      value: filter.value ?? null,
    })),
  };
};

export type DatatableState = z.infer<typeof DatatableStateSchema>;

export function compareDatatableStates(state1: DatatableState, state2?: DatatableState | null | undefined): boolean {
  if (!state2) {
    return false;
  }

  // Clean both states using getCleanedDatatableState to ensure consistent normalization
  // (strips __typename, normalizes undefined filter values to null)
  const cleanedState1 = getCleanedDatatableState(state1);
  const cleanedState2 = getCleanedDatatableState(state2);

  if (!cleanedState1 || !cleanedState2) {
    return false;
  }

  // Compare individual fields to avoid JSON.stringify key-order sensitivity
  return (
    cleanedState1.showColumnHeaders === cleanedState2.showColumnHeaders &&
    (cleanedState1.sectionColumnId ?? null) === (cleanedState2.sectionColumnId ?? null) &&
    (cleanedState1.subSectionColumnId ?? null) === (cleanedState2.subSectionColumnId ?? null) &&
    cleanedState1.globalFilter === cleanedState2.globalFilter &&
    compareExpanded(cleanedState1.expanded, cleanedState2.expanded) &&
    JSON.stringify(cleanedState1.columnOrder) === JSON.stringify(cleanedState2.columnOrder) &&
    JSON.stringify(cleanedState1.sorting) === JSON.stringify(cleanedState2.sorting) &&
    JSON.stringify(cleanedState1.columnFilters) === JSON.stringify(cleanedState2.columnFilters) &&
    compareRecords(cleanedState1.columnVisibility, cleanedState2.columnVisibility)
  );
}

function compareExpanded(a: DatatableState["expanded"], b: DatatableState["expanded"]): boolean {
  if (a === true && b === true) {
    return true;
  }
  if (a === true || b === true) {
    return false;
  }
  return compareRecords(a, b);
}

function compareRecords(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  return keysA.every((key) => a[key] === b[key]);
}

export function mergeDatatableStates(state1: DatatableState, state2: DatatableState): DatatableState {
  return {
    ...state1,
    ...state2,
  };
}

/**
 * Filters a DatatableState to only include references to valid column IDs.
 * Removes invalid column IDs from columnOrder, columnVisibility, columnFilters,
 * sorting, sectionColumnId, and subSectionColumnId.
 */
export function filterDatatableStateByColumns(state: DatatableState, validColumnIdSet: Set<string>): DatatableState {
  const filteredColumnVisibility: Record<string, boolean> = {};
  for (const [id, value] of Object.entries(state.columnVisibility)) {
    if (validColumnIdSet.has(id)) {
      filteredColumnVisibility[id] = value;
    }
  }

  return {
    ...state,
    columnOrder: state.columnOrder.filter((id) => validColumnIdSet.has(id)),
    columnVisibility: filteredColumnVisibility,
    columnFilters: state.columnFilters.filter((filter) => validColumnIdSet.has(filter.id)),
    sorting: state.sorting.filter((sort) => validColumnIdSet.has(sort.id)),
    sectionColumnId:
      state.sectionColumnId && validColumnIdSet.has(state.sectionColumnId) ? state.sectionColumnId : null,
    subSectionColumnId:
      state.subSectionColumnId && validColumnIdSet.has(state.subSectionColumnId) ? state.subSectionColumnId : null,
  };
}
